import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { apiGet, apiPatch } from '../lib/api.js';
import {
  applyThemePreference,
  readStoredThemePreference,
  resolveThemePreference,
  writeStoredThemePreference,
  type ResolvedTheme,
  type ThemePreference,
} from '../lib/theme.js';
import { useAuthStore } from '../store/authStore.js';

type ThemeContextValue = {
  themePreference: ThemePreference;
  resolvedTheme: ResolvedTheme;
  setThemePreference: (preference: ThemePreference, options?: { persistRemote?: boolean }) => void;
  syncThemeFromServer: (preference: ThemePreference) => void;
};

const ThemeContext = createContext<ThemeContextValue | null>(null);

export function ThemeProvider({ children }: { children: ReactNode }) {
  const user = useAuthStore((s) => s.user);
  const isInitialized = useAuthStore((s) => s.isInitialized);
  const [themePreference, setThemePreferenceState] = useState<ThemePreference>(
    () => readStoredThemePreference() ?? 'dark',
  );
  const [resolvedTheme, setResolvedTheme] = useState<ResolvedTheme>(() =>
    resolveThemePreference(readStoredThemePreference() ?? 'dark'),
  );
  const remoteSyncDone = useRef(false);

  const applyLocal = useCallback((preference: ThemePreference) => {
    writeStoredThemePreference(preference);
    const resolved = applyThemePreference(preference);
    setThemePreferenceState(preference);
    setResolvedTheme(resolved);
  }, []);

  const setThemePreference = useCallback(
    (preference: ThemePreference, options?: { persistRemote?: boolean }) => {
      applyLocal(preference);
      const shouldPersist = options?.persistRemote !== false && !!useAuthStore.getState().user;
      if (shouldPersist) {
        void apiPatch('/users/me/preferences', { themePreference: preference }).catch(() => {
          // Preferences page / hook surfaces errors; silent here for nav toggles.
        });
      }
    },
    [applyLocal],
  );

  const syncThemeFromServer = useCallback(
    (preference: ThemePreference) => {
      applyLocal(preference);
    },
    [applyLocal],
  );

  useEffect(() => {
    if (themePreference !== 'auto') return;
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => {
      const resolved = resolveThemePreference('auto');
      applyThemePreference('auto');
      setResolvedTheme(resolved);
    };
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, [themePreference]);

  useEffect(() => {
    if (!isInitialized || !user) {
      remoteSyncDone.current = false;
      return;
    }
    if (remoteSyncDone.current) return;
    remoteSyncDone.current = true;

    void (async () => {
      try {
        const prefs = await apiGet<{ themePreference: ThemePreference }>('/users/me/preferences');
        if (prefs.themePreference) {
          applyLocal(prefs.themePreference);
        }
      } catch {
        // Keep localStorage theme when offline or unauthorized.
      }
    })();
  }, [isInitialized, user, applyLocal]);

  const value = useMemo(
    () => ({
      themePreference,
      resolvedTheme,
      setThemePreference,
      syncThemeFromServer,
    }),
    [themePreference, resolvedTheme, setThemePreference, syncThemeFromServer],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return ctx;
}
